import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../../../api/client'; // Adjust path as needed
import '../../../css/alertpages.css';

const MotionDiv = motion.div;

const BetaDialog = ({ onClose, showImmediately = false }) => {
  const [isVisible, setIsVisible] = useState(showImmediately);
  const [formData, setFormData] = useState({
    email: '',
    countryCode: '+65', // Default to Singapore
    mobile: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Country codes with flags and names
  const countryCodes = [
    { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
    { code: '+60', country: 'MY', name: 'Malaysia', flag: '🇲🇾' },
    { code: '+62', country: 'ID', name: 'Indonesia', flag: '🇮🇩' },
    { code: '+66', country: 'TH', name: 'Thailand', flag: '🇹🇭' },
    { code: '+84', country: 'VN', name: 'Vietnam', flag: '🇻🇳' },
    { code: '+63', country: 'PH', name: 'Philippines', flag: '🇵🇭' },
    { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
    { code: '+852', country: 'HK', name: 'Hong Kong', flag: '🇭🇰' },
    { code: '+886', country: 'TW', name: 'Taiwan', flag: '🇹🇼' },
    { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
    { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' }
  ];

  useEffect(() => {
    // Only use timer if not showing immediately and no onClose prop
    if (!showImmediately && !onClose) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 7000);

      return () => clearTimeout(timer);
    }
  }, [showImmediately, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/api/beta/signup', {
        email: formData.email,
        countryCode: formData.countryCode,
        mobile: formData.mobile
      });
      
      if (response.data.success) {
        setIsSubmitted(true);
        
        // Auto close after showing thank you message
        setTimeout(() => {
          handleClose();
        }, 4000);
      }
    } catch (error) {
      console.error('Beta signup error:', error);
      setError(error.response?.data?.message || 'Failed to sign up. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // Call the onClose callback if provided (for GetStarted page)
    if (onClose) {
      onClose();
    }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { 
      scale: 0.8, 
      opacity: 0,
      y: 50
    },
    visible: { 
      scale: 1, 
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300
      }
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <MotionDiv 
          className="beta-modal-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={handleClose}
        >
          <MotionDiv 
            className="beta-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="beta-modal-close" onClick={handleClose}>
              ✕
            </button>
            
            {!isSubmitted ? (
              <>
                <div className="beta-modal-header">
                  <h3>Wah! Thanks for checking out Poslah! 🙌</h3>
                </div>
                
                <div className="beta-modal-body">
                  <p>
                    We're currently in <strong>Phase 1 beta testing</strong> our awesome POS system. 
                    Want to be among the first to try it when we launch?
                  </p>
                  
                  {error && (
                    <MotionDiv 
                      className="beta-error-message"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      {error}
                    </MotionDiv>
                  )}
                  
                  <form onSubmit={handleSubmit} className="beta-email-form">
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email *"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="beta-email-input"
                      required
                      disabled={isLoading}
                    />
                    
                    <div className="beta-mobile-group">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        className="beta-country-select"
                        disabled={isLoading}
                      >
                        {countryCodes.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        name="mobile"
                        placeholder="Mobile number (optional)"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        className="beta-mobile-input"
                        disabled={isLoading}
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      className="beta-submit-btn"
                      disabled={isLoading || !formData.email}
                    >
                      {isLoading ? (
                        <>
                          <span className="beta-loading-spinner"></span>
                          Signing up...
                        </>
                      ) : (
                        "I want to know more! 🚀"
                      )}
                    </button>
                  </form>
                  
                  <p className="beta-disclaimer">
                    *No spam lah, promise! 😊*
                  </p>
                </div>
                
                <div className="beta-modal-footer">
                  <button className="beta-explore-btn" onClick={handleClose}>
                    Explore First
                  </button>
                </div>
              </>
            ) : (
              <div className="beta-thank-you">
                <h3>Shiok! Thanks for signing up! 🎉</h3>
                <p>We'll contact you soon with exclusive early access!</p>
                <p className="beta-email-note">Check your email for confirmation! 📧</p>
              </div>
            )}
          </MotionDiv>
        </MotionDiv>
      )}
    </AnimatePresence>
  );
};

export default BetaDialog;